// 일괄 생성 확인 단계: 같은 학생이 여러 항목으로 들어온 경우(보고서·활동지를 따로 올림, 한 학생 답안이 둘로 나뉨)를 찾아 합친다.

const PLACEHOLDER_NAME = /^학생\s*\d*$/;

const contentOf = (student) => String(student?.existing_content || student?.content || '').trim();

export const normalizeStudentNameKey = (name) => {
  const key = String(name || '').normalize('NFC').replace(/\s+/g, '');
  return !key || PLACEHOLDER_NAME.test(key) ? '' : key;
};

// 김*수, 김○수, 김O수처럼 일부를 가린 이름은 서로 다른 학생도 같은 이름이 된다.
const MASK_CHARS = /[*＊○●◯〇◎□■×]/;
const LETTER_MASK = /^[가-힣][OoXxㅇ]+[가-힣]?$/;

export const isMaskedStudentName = (name) => {
  const key = normalizeStudentNameKey(name);
  return Boolean(key) && (MASK_CHARS.test(key) || LETTER_MASK.test(key));
};

// [{ key, name, indexes }] — 이름이 같은 항목이 2개 이상인 그룹만, 처음 나온 순서대로.
export const findDuplicateStudentGroups = (students = []) => {
  const groups = new Map();
  students.forEach((student, index) => {
    const key = normalizeStudentNameKey(student?.name);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, { key, name: String(student.name).trim(), indexes: [] });
    groups.get(key).indexes.push(index);
  });
  return [...groups.values()].filter((group) => group.indexes.length > 1);
};

const uniqueList = (values) => [...new Set(values.filter((value) => value !== undefined && value !== null && value !== ''))];

const mergeGroup = (members) => {
  const [first] = members;
  const contents = uniqueList(members.map(contentOf));
  const merged = contents.join('\n\n');
  const pages = uniqueList(members.flatMap((member) => member.source_pages || []))
    .sort((a, b) => Number(a) - Number(b));
  const confidences = members.map((member) => member.confidence).filter((value) => typeof value === 'number');
  return {
    ...first,
    existing_content: merged,
    content: merged,
    warnings: uniqueList(members.flatMap((member) => member.warnings || [])),
    included_columns: uniqueList(members.flatMap((member) => member.included_columns || [])),
    ...(pages.length > 0 ? { source_pages: pages } : {}),
    ...(confidences.length > 0 ? { confidence: Math.min(...confidences) } : {}),
    merged_count: members.reduce((sum, member) => sum + (member.merged_count || 1), 0),
    _recognitionEdited: true,
  };
};

// 같은 이름 항목을 처음 나온 자리에 하나로 합친다. 내용이 똑같은 항목은 한 번만 남긴다.
// onlyKeys(Set)를 주면 그 이름만 합치고 나머지(동명이인)는 그대로 둔다.
export const mergeDuplicateStudents = (students = [], onlyKeys = null) => {
  const groups = findDuplicateStudentGroups(students)
    .filter((group) => !onlyKeys || onlyKeys.has(group.key));
  if (groups.length === 0) return students;
  const firstIndexOf = new Map();
  const skip = new Set();
  groups.forEach((group) => {
    firstIndexOf.set(group.indexes[0], group);
    group.indexes.slice(1).forEach((index) => skip.add(index));
  });
  return students.reduce((next, student, index) => {
    if (skip.has(index)) return next;
    const group = firstIndexOf.get(index);
    next.push(group ? mergeGroup(group.indexes.map((i) => students[i])) : student);
    return next;
  }, []);
};
